import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { toNano } from '@ton/core';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { useStore } from 'app/store';
import { isNil } from 'lodash';
import { observer } from 'mobx-react';
import { useForm } from 'react-hook-form';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '~features/form';
import { useToast } from '~features/toaster';
import { SharedService, type CreateWithdrawTransactionSchema } from '~shared/api/client';
import { TonIcon } from '~shared/assets/icons/currency';
import { Button, Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, Input } from '~shared/ui';

import { getDepositTonSchema, type DepositTonValues } from '../model';

export const DepositTonDrawer: React.FC = observer(() => {
  const {
    userStore: { user, getUser, closeDepositTonDrawer, isDepositTonDrawerOpen },
  } = useStore();

  const { toast } = useToast();

  const tonAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();

  const [isDepositLoading, setIsDepositLoading] = useState(false);

  const [depositAddress, setDepositAddress] = useState<string | null>(null);

  const depositTonSchema = useMemo(() => getDepositTonSchema(), []);

  const depositTonForm = useForm<DepositTonValues>({
    defaultValues: { amount: '' },
    mode: 'onTouched',
    resolver: zodResolver(depositTonSchema),
  });

  const handleClose = useCallback(() => {
    closeDepositTonDrawer();
    depositTonForm.reset({ amount: '' });
    setIsDepositLoading(false);
  }, [depositTonForm, closeDepositTonDrawer]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        handleClose();
      }
    },
    [handleClose]
  );

  const getDepositAddress = useCallback(async () => {
    try {
      const { address } = await SharedService.getTonDepositInfoApiV1SharedTonDepositInfoGet();
      setDepositAddress(address);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleTonDepositSubmit = useCallback(
    async (values: CreateWithdrawTransactionSchema) => {
      try {
        if (isNil(user) || isNil(user.id) || isNil(depositAddress)) return;
        setIsDepositLoading(true);
        await tonConnectUI.sendTransaction(
          {
            messages: [
              {
                address: depositAddress,
                amount: toNano(values.amount).toString(),
              },
            ],
            from: tonAddress,
            validUntil: Math.floor(Date.now() / 1000) + 360,
          },
          { returnStrategy: 'back' }
        );

        await getUser();
        handleClose();
        toast({
          variant: 'default',
          title: 'Sucess',
          description: 'Deposit transaction is sent',
        });
      } catch (error) {
        toast({ variant: 'destructive', description: 'Failed to send a transaction' });
      } finally {
        setIsDepositLoading(false);
      }
    },
    [toast, user, getUser, tonConnectUI, depositAddress, tonAddress, handleClose]
  );

  useEffect(() => {
    void getDepositAddress();
  }, [getDepositAddress]);

  return (
    <Drawer open={isDepositTonDrawerOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className="pb-0" open={isDepositTonDrawerOpen}>
        <div className="flex w-full flex-1 flex-col overflow-auto pb-12">
          <DrawerHeader className="space-y-4">
            <TonIcon className="mx-auto size-20" />
            <DrawerTitle>TON Deposit</DrawerTitle>
          </DrawerHeader>

          <Form {...depositTonForm}>
            <form className="flex flex-col space-y-5" onSubmit={depositTonForm.handleSubmit(handleTonDepositSubmit)}>
              <FormField
                name="amount"
                control={depositTonForm.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DrawerFooter>
                <Button type="submit" buttonColor="blue" isLoading={isDepositLoading}>
                  Deposit
                </Button>
              </DrawerFooter>
            </form>
          </Form>
        </div>
      </DrawerContent>
    </Drawer>
  );
});
